import { CardBody, Heading, HStack, LinkOverlay, Text, VStack } from '@oaknorth/component-core-v2';
import { Link } from '@oaknorth/util-routing';

import { useClientFeatures } from './features-hooks';
import { HomePageCard, HomePageCardType } from './HomePageCard';
import { ClientFeatureSpec } from './types';

const FEATURE_ICON_SIZE = 20;

export function FeatureCard({
  name,
  to,
  description,
  icon: FeatureIcon,
  additionalActions,
  type,
  isLocked,
}: ClientFeatureSpec & { type: HomePageCardType }) {
  return (
    <HomePageCard to={isLocked ? undefined : to} type={type} isLocked={isLocked}>
      <CardBody>
        <HStack
          paddingY={2}
          paddingX={type === HomePageCardType.WIDE ? 2 : undefined}
          gap={8}
          alignItems="flex-start"
          data-testid="home-page-client-feature-card"
        >
          <FeatureIcon
            color={isLocked ? 'fg.muted' : 'blue.500'}
            viewBox="0 0 101 101"
            height={FEATURE_ICON_SIZE}
            width={FEATURE_ICON_SIZE}
          />
          <VStack alignItems="flex-start">
            <LinkOverlay asChild>
              <Heading as="h3" color={isLocked ? 'fg.muted' : 'blue.500'} fontWeight="normal" fontSize="lg">
                <Link to={to}>{name}</Link>
              </Heading>
            </LinkOverlay>
            <Text fontWeight="normal" color={isLocked ? 'fg.muted' : 'chakra-body-text'}>
              {description}
            </Text>
            {additionalActions}
          </VStack>
        </HStack>
      </CardBody>
    </HomePageCard>
  );
}

// The home page grid consists of 6 columns total.
// If the number of feature cards is divible by three, show 3 columns per grid row (colspan 2 for each card)
// Otherwise, if the number of feature cards is divible by three, show 2 columns per grid row (colspan 3 for each card)
// If the number of feature cards is an odd number not divisible by 3, show 2 columns per row except for the last row
// (in the last row, show 3 cards)
function getFeatureCardType(featureCount: number, currentIndex: number) {
  if (featureCount % 3 === 0) {
    return HomePageCardType.NARROW;
  }
  if (featureCount % 2 === 0) {
    return HomePageCardType.WIDE;
  }
  return currentIndex >= featureCount - 3 ? HomePageCardType.NARROW : HomePageCardType.WIDE;
}

export function ClientFeatures() {
  const clientFeatures = useClientFeatures();
  const featureCount = clientFeatures.length;
  return (
    <>
      {clientFeatures.map((spec, index) => {
        return <FeatureCard {...spec} key={spec.key} type={getFeatureCardType(featureCount, index)} />;
      })}
    </>
  );
}
